// src/components/mensagens/DeleteConfirmModal.jsx
// ============================================================================
// 🎯 COMPONENTE: DeleteConfirmModal — V2 ENTERPRISE PORTAL
// 📋 Função: Modal de confirmação de exclusão reutilizável
// 📱 Responsivo: Bottom Sheet nativo em mobile · Modal centrado em desktop
// 🎨 Design: Glassmorphism · Animações GPU-accelerated · Haptic feedback
// 🔒 Robusto: Anti-crash · Validação de props · Acessibilidade total
// 🔥 V2: Portal a document.body · Dark mode automático
// ============================================================================

import React, { useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  Trash2,
  X,
  MessageSquare,
  User,
  Clock,
  Loader2,
  ShieldAlert,
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useTheme } from '../../contexts/ThemeContext';
import { usePortal, useBodyScrollLock, useEscapeKey, portalize } from '../../utils/portalUtils';
import { truncateText, formatFullDateTime } from './helpers';

// ============================================================================
// 🎨 CONSTANTES
// ============================================================================
const COLORS = {
  danger: {
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-800',
    icon: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-100 dark:bg-red-900/40',
    button: 'bg-red-600 hover:bg-red-700 dark:hover:bg-red-500',
    buttonLight: 'bg-red-500/10 hover:bg-red-500/20 text-red-600 border border-red-200',
    gradient: 'from-red-500 to-rose-600',
    focus: 'ring-red-400',
  },
};

// ============================================================================
// 🎯 TIPOS DE CONFIRMAÇÃO
// ============================================================================
const CONFIG = {
  message: {
    title: 'Excluir mensagem',
    description: 'Esta ação não pode ser desfeita.',
    icon: MessageSquare,
    getBodyText: (data) => {
      const preview = truncateText(data?.message?.message || '', 80);
      const time = data?.message?.createdAt
        ? formatFullDateTime(data.message.createdAt)
        : null;
      return {
        preview,
        time,
        meta: data?.clientName ? `Cliente: ${data.clientName}` : null,
      };
    },
  },
  conversation: {
    title: 'Excluir conversa',
    description: 'Todas as mensagens deste cliente serão removidas permanentemente.',
    icon: User,
    getBodyText: (data) => {
      const count = data?.count || 0;
      return {
        preview: `${count} mensagen${count !== 1 ? 's' : ''} serão excluídas`,
        time: null,
        meta: data?.clientName || data?.clientPhone
          ? `${data.clientName || ''} ${data.clientPhone || ''}`.trim()
          : null,
      };
    },
  },
};

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL
// ============================================================================
function DeleteConfirmModal({
  isOpen = false,
  type = 'message',
  data = null,
  onConfirm,
  onCancel,
  loading = false,
  primaryColor = '#ef4444',
}) {
  const isMobile = useIsMobile();
  const { isDark } = useTheme();
  const mounted = usePortal();
  const cancelBtnRef = useRef(null);
  const config = CONFIG[type] || CONFIG.message;
  const colors = COLORS.danger;
  const IconComponent = config.icon;
  const body = config.getBodyText(data);

  // ✅ Portal hooks
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, loading ? null : onCancel);

  // ==========================================================================
  // 🎮 FOCAR NO BOTÃO CANCELAR AO ABRIR — Acessibilidade
  // ==========================================================================
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => cancelBtnRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // ==========================================================================
  // ⚠️ VALIDAÇÃO DE SEGURANÇA
  // ==========================================================================
  if (!isOpen) return null;

  if (typeof onConfirm !== 'function' || typeof onCancel !== 'function') {
    console.error(
      '❌ [DeleteConfirmModal] Props obrigatórias: onConfirm e onCancel devem ser funções'
    );
    return null;
  }

  // ==========================================================================
  // 🎨 ANIMAÇÕES — GPU Accelerated
  // ==========================================================================
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const mobileSheetVariants = {
    hidden: { y: '100%', opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', damping: 30, stiffness: 300, mass: 0.9 },
    },
    exit: {
      y: '100%',
      opacity: 0,
      transition: { type: 'spring', damping: 28, stiffness: 250, mass: 1 },
    },
  };

  const desktopModalVariants = {
    hidden: { scale: 0.92, y: 24, opacity: 0 },
    visible: {
      scale: 1,
      y: 0,
      opacity: 1,
      transition: { type: 'spring', damping: 26, stiffness: 350, mass: 0.8 },
    },
    exit: {
      scale: 0.92,
      y: 24,
      opacity: 0,
      transition: { duration: 0.15, ease: 'easeIn' },
    },
  };

  // 🌓 Clases dark mode
  const bgSheet = "bg-white dark:bg-slate-900";
  const bgCard = "bg-gray-50 dark:bg-slate-800/50";
  const borderCard = "border-gray-100 dark:border-slate-700";
  const textDark = "text-gray-900 dark:text-slate-100";
  const textGray = "text-gray-500 dark:text-slate-400";
  const textGrayStrong = "text-gray-700 dark:text-slate-300";
  const textPreview = "text-gray-800 dark:text-slate-200";
  const bgWarning = "bg-amber-50 dark:bg-amber-950/30";
  const borderWarning = "border-amber-100 dark:border-amber-800";
  const textWarning = "text-amber-700 dark:text-amber-300";
  const textWarningStrong = "text-amber-800 dark:text-amber-200";
  const bgWarningIcon = "bg-amber-100 dark:bg-amber-900/40";
  const btnCancel = "border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 active:bg-gray-100 dark:active:bg-slate-700";
  const handleBar = "bg-gray-300 dark:bg-slate-600";

  // ==========================================================================
  // 🖼️ RENDER
  // ==========================================================================
  const modalContent = (
    <AnimatePresence>
      <motion.div
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.18 }}
        className="fixed inset-0 z-[99999] flex items-center justify-center"
        onClick={loading ? undefined : onCancel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        {/* Overlay com blur */}
        <div
          className={`
            absolute inset-0
            ${isMobile ? 'bg-black/70 dark:bg-black/85' : 'bg-black/60 dark:bg-black/80'}
            backdrop-blur-md
            transition-all duration-200
          `}
          style={{ willChange: 'opacity' }}
        />

        {/* ================================================================ */}
        {/* 📱 MOBILE: Bottom Sheet Nativo */}
        {/* ================================================================ */}
        {isMobile ? (
          <motion.div
            variants={mobileSheetVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`
              absolute bottom-0 left-0 right-0
              ${bgSheet} rounded-t-3xl
              max-h-[85vh] overflow-y-auto
              shadow-2xl
              border-t border-white/20 dark:border-slate-700
            `}
            style={{
              contain: 'layout style paint',
              willChange: 'transform',
              boxShadow: '0 -20px 60px -10px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle de arraste visual */}
            <div className="flex justify-center pt-3 pb-1">
              <div className={`w-10 h-1.5 ${handleBar} rounded-full`} />
            </div>

            {/* Header */}
            <div className="px-5 pt-2 pb-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 ${colors.iconBg} rounded-2xl`}>
                  <IconComponent size={20} className={colors.icon} />
                </div>
                <div>
                  <h2
                    id="delete-modal-title"
                    className={`text-lg font-bold ${textDark} leading-tight`}
                  >
                    {config.title}
                  </h2>
                </div>
              </div>
              <button
                onClick={loading ? undefined : onCancel}
                disabled={loading}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition-colors disabled:opacity-40"
                aria-label="Fechar"
              >
                <X size={20} className="text-gray-400 dark:text-slate-500" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 pb-4">
              <p
                id="delete-modal-description"
                className={`text-sm ${textGray} leading-relaxed mb-4`}
              >
                {config.description}
              </p>

              {/* Card de preview */}
              {body && (
                <div className={`${bgCard} rounded-2xl p-4 border ${borderCard} mb-2`}>
                  {body.preview && (
                    <p className={`text-sm ${textPreview} font-medium mb-2 break-words`}>
                      {body.preview}
                    </p>
                  )}
                  {body.time && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-slate-500 mb-1">
                      <Clock size={12} />
                      <span>{body.time}</span>
                    </div>
                  )}
                  {body.meta && (
                    <p className={`text-xs ${textGray} flex items-center gap-1`}>
                      <span className="w-1 h-1 bg-gray-400 dark:bg-slate-500 rounded-full" />
                      {body.meta}
                    </p>
                  )}
                </div>
              )}

              {/* Alerta de segurança */}
              <div className={`flex items-start gap-2 p-3 ${bgWarning} rounded-xl border ${borderWarning} mb-2`}>
                <ShieldAlert size={16} className="text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className={`text-xs ${textWarning} leading-relaxed`}>
                  Esta ação é permanente e não poderá ser desfeita.
                </p>
              </div>
            </div>

            {/* Botões — Layout mobile otimizado */}
            <div className="px-5 pb-6 pt-2 flex gap-3">
              <button
                ref={cancelBtnRef}
                onClick={loading ? undefined : onCancel}
                disabled={loading}
                className={`flex-1 px-4 py-3.5 rounded-2xl border ${btnCancel} font-semibold text-sm
                           transition-all disabled:opacity-40 disabled:cursor-not-allowed
                           focus-visible:ring-2 focus-visible:ring-gray-300 dark:focus-visible:ring-slate-500 focus-visible:outline-none`}
              >
                Cancelar
              </button>
              <button
                onClick={loading ? undefined : onConfirm}
                disabled={loading}
                className={`
                  flex-1 px-4 py-3.5 rounded-2xl text-white font-semibold text-sm
                  flex items-center justify-center gap-2
                  transition-all active:scale-[0.97]
                  disabled:opacity-50 disabled:cursor-not-allowed
                  focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 focus-visible:outline-none
                  ${colors.button}
                `}
                style={{ contain: 'layout style paint' }}
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Excluindo...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    <span>Excluir</span>
                  </>
                )}
              </button>
            </div>

            {/* Safe area para iPhones com notch */}
            <div className="h-6 pb-safe" />
          </motion.div>
        ) : (
          /* ================================================================ */
          /* 🖥️ DESKTOP: Modal Centrado com Glassmorphism */
          /* ================================================================ */
          <motion.div
            variants={desktopModalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="relative bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-3xl w-full max-w-lg mx-4
                       shadow-2xl border border-white/20 dark:border-slate-700/20 overflow-hidden"
            style={{
              contain: 'layout style paint',
              willChange: 'transform, opacity',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Barra de gradiente superior */}
            <div
              className={`h-1.5 bg-gradient-to-r ${colors.gradient}`}
              style={{ willChange: 'transform' }}
            />

            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center gap-4">
                  <div className={`p-3 ${colors.iconBg} rounded-2xl shadow-sm`}>
                    <IconComponent size={24} className={colors.icon} />
                  </div>
                  <div>
                    <h2
                      id="delete-modal-title"
                      className={`text-xl font-bold ${textDark}`}
                    >
                      {config.title}
                    </h2>
                    <p className={`text-sm ${textGray} mt-0.5`}>{config.description}</p>
                  </div>
                </div>
                <button
                  onClick={loading ? undefined : onCancel}
                  disabled={loading}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition-colors disabled:opacity-40
                             focus-visible:ring-2 focus-visible:ring-gray-300 dark:focus-visible:ring-slate-500 focus-visible:outline-none"
                  aria-label="Fechar"
                >
                  <X size={20} className="text-gray-400 dark:text-slate-500" />
                </button>
              </div>

              {/* Body */}
              {body && (
                <div className={`${bgCard} rounded-2xl p-4 border ${borderCard} mb-4`}>
                  {body.preview && (
                    <p className={`text-sm ${textPreview} font-medium mb-2 break-words leading-relaxed`}>
                      {body.preview}
                    </p>
                  )}
                  {body.time && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-slate-500 mb-1">
                      <Clock size={12} />
                      <span>{body.time}</span>
                    </div>
                  )}
                  {body.meta && (
                    <p className={`text-xs ${textGray} flex items-center gap-1.5`}>
                      <User size={11} />
                      {body.meta}
                    </p>
                  )}
                </div>
              )}

              {/* Alerta de segurança */}
              <div className={`flex items-start gap-3 p-3.5 ${bgWarning} rounded-2xl border ${borderWarning} mb-6`}>
                <div className={`p-1 ${bgWarningIcon} rounded-lg flex-shrink-0 mt-0.5`}>
                  <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className={`text-xs font-semibold ${textWarningStrong} mb-0.5`}>
                    Atenção
                  </p>
                  <p className={`text-xs ${textWarning} leading-relaxed`}>
                    Esta ação é permanente e não poderá ser desfeita. Todos os dados
                    associados serão removidos.
                  </p>
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3 justify-end">
                <button
                  ref={cancelBtnRef}
                  onClick={loading ? undefined : onCancel}
                  disabled={loading}
                  className={`px-5 py-2.5 rounded-xl border ${btnCancel} font-semibold text-sm
                             transition-all disabled:opacity-40 disabled:cursor-not-allowed
                             focus-visible:ring-2 focus-visible:ring-gray-300 dark:focus-visible:ring-slate-500 focus-visible:outline-none`}
                >
                  Cancelar
                </button>
                <button
                  onClick={loading ? undefined : onConfirm}
                  disabled={loading}
                  className={`
                    px-6 py-2.5 rounded-xl text-white font-semibold text-sm
                    flex items-center gap-2
                    transition-all active:scale-[0.97]
                    disabled:opacity-50 disabled:cursor-not-allowed
                    focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 focus-visible:outline-none
                    ${colors.button}
                  `}
                  style={{ contain: 'layout style paint' }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Excluindo...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      <span>Sim, excluir</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );

  return portalize(mounted, modalContent);
}

// ============================================================================
// 🎯 MEMO + DISPLAY NAME
// ============================================================================
DeleteConfirmModal.displayName = 'DeleteConfirmModal';

// ============================================================================
// 🎯 EXPORTAÇÃO
// ============================================================================
export default React.memo(DeleteConfirmModal);